import { useHistory, useLocation } from "react-router-dom";
import type { z } from "zod";

/**
 * Consume the current entry's router state — for state carrying a COMMAND
 * ("submit this question on arrival") rather than display data. `history.state`
 * is durable: Chrome restores it across reloads and even full browser
 * restarts, so a command left on the entry replays its action on every
 * restore. Call the returned function inside the effect that fires the
 * action, BEFORE acting, so the command is one-shot however the entry comes
 * back. (Left in place, the Chat-with-Paper landing re-submitted its
 * handed-over question on every browser reopen — each replay persisted as a
 * real turn.)
 */
export function useConsumeLocationState(): () => void {
  const history = useHistory();
  return () => {
    const { pathname, search, hash } = history.location;
    history.replace({ pathname, search, hash });
  };
}

// react-router-dom types `location.state` as the caller-supplied generic, but
// at runtime it is `undefined` on direct URL entry, hard reload (iOS Safari
// discards state on reload), Safe-Links pre-fetches, and any external deep
// link. When state is missing, try `undefined` first (triggers object-level
// `.default()`), then `{}` (triggers field-level `.default()` inside objects).
export function useLocationState<Schema extends z.ZodTypeAny>(
  schema: Schema
): z.infer<Schema> {
  const location = useLocation();
  const missing = location.state === undefined || location.state === null;

  if (!missing) {
    const result = schema.safeParse(location.state);
    if (result.success) return result.data;
    throw new Error(
      `useLocationState: router state did not match schema — ${result.error.message}`
    );
  }

  const withUndefined = schema.safeParse(undefined);
  if (withUndefined.success) return withUndefined.data;

  const withEmpty = schema.safeParse({});
  if (withEmpty.success) return withEmpty.data;

  throw new Error(
    `useLocationState: router state did not match schema — ${withEmpty.error.message}`
  );
}
